import axios from 'axios';

export type DeadlineTimerTick = {
	minutes: string;
	seconds: string;
	remainingMs: number;
};

type Subscriber = (tick: DeadlineTimerTick) => void;

type TimerData = {
	server_timestamp: number;
	deadline_timestamp: number;
};

/**
 * Engine singleton global para garantir que só exista 1 contador rodando na aplicação,
 * mesmo que múltiplas instâncias do componente sejam montadas.
 */
export class DeadlineTimerEngine {
	private static _instance: DeadlineTimerEngine | null = null;

	static instance() {
		if (!this._instance) this._instance = new DeadlineTimerEngine();
		return this._instance;
	}

	private subscribers = new Set<Subscriber>();
	private pendingSync: Promise<void> | null = null;
	private tickIntervalId: NodeJS.Timeout | undefined;
	private pollTimeoutId: NodeJS.Timeout | undefined;
	private hasEmittedExpired = false;
	private expireEmissionClaimed = false;
	private isVerifyingZero = false;
	private stopped = true;
	private generationId = 0;

	private getTimerDataUrl: string | null = null;

	// Âncoras baseadas no servidor
	private serverTimeMs = 0;
	private deadlineTimeMs = 0;
	private remainingMs = 0;

	/**
	 * Último tick emitido (para entregar imediatamente ao subscribe).
	 */
	private lastTick: DeadlineTimerTick = { minutes: '00', seconds: '00', remainingMs: 0 };

	configure(getTimerDataUrl: string) {
		this.getTimerDataUrl = getTimerDataUrl;
	}

	subscribe(cb: Subscriber) {
		this.subscribers.add(cb);
		// entrega estado atual imediatamente
		cb(this.lastTick);
		return () => {
			this.subscribers.delete(cb);
			// se ninguém mais estiver ouvindo, para tudo para evitar leak
			if (this.subscribers.size === 0) {
				this.stop();
			}
		};
	}

	/**
	 * Start idempotente: se já está rodando, não cria um segundo interval.
	 */
	async start(): Promise<void> {
		if (!this.getTimerDataUrl) {
			throw new Error('[deadline_timer_engine] getTimerDataUrl não configurado');
		}
		if (!this.stopped) return;

		this.stopped = false;
		this.hasEmittedExpired = false;
		this.expireEmissionClaimed = false;
		this.isVerifyingZero = false;
		this.generationId++;
		const localGeneration = this.generationId;

		this.clearTimers();
		await this.syncNow(localGeneration);
		if (this.stopped || localGeneration !== this.generationId) return;

		this.renderAndPublish();

		if (this.remainingMs === 0) {
			void this.verifyZeroAndMaybeExpire(localGeneration);
			return;
		}

		this.startTick(localGeneration);
		this.startPolling(localGeneration);
	}

	stop() {
		if (this.stopped) return;
		this.stopped = true;
		this.generationId++;
		this.clearTimers();
	}

	/**
	 * Permite o componente registrar um handler de expiração (1 vez).
	 */
	get expired(): boolean {
		return this.hasEmittedExpired;
	}

	/**
	 * Garante que apenas 1 consumidor (ex.: 1 instância do componente) emita a ação de expiração.
	 * Retorna true apenas uma vez, após a expiração ter sido confirmada.
	 */
	claimExpireEmission(): boolean {
		if (!this.hasEmittedExpired) return false;
		if (this.expireEmissionClaimed) return false;
		this.expireEmissionClaimed = true;
		return true;
	}

	private clearTimers() {
		if (this.tickIntervalId) {
			clearInterval(this.tickIntervalId);
			this.tickIntervalId = undefined;
		}
		if (this.pollTimeoutId) {
			clearTimeout(this.pollTimeoutId);
			this.pollTimeoutId = undefined;
		}
	}

	private async fetchTimerData(): Promise<TimerData> {
		if (!this.getTimerDataUrl) {
			throw new Error('[deadline_timer_engine] getTimerDataUrl não configurado');
		}
		return axios.get(this.getTimerDataUrl).then((r) => r.data);
	}

	private async syncNow(localGeneration: number): Promise<void> {
		if (this.pendingSync) return this.pendingSync;

		this.pendingSync = (async () => {
			try {
				const data = await this.fetchTimerData();
				if (this.stopped || localGeneration !== this.generationId) return;
				if (typeof data?.server_timestamp === 'number') {
					this.serverTimeMs = data.server_timestamp;
				}
				if (typeof data?.deadline_timestamp === 'number') {
					this.deadlineTimeMs = data.deadline_timestamp;
				}
				this.remainingMs = Math.max(0, this.deadlineTimeMs - this.serverTimeMs);
			} catch (error) {
				console.warn('[deadline_timer_engine] Falha ao sincronizar timer-data', error);
			}
		})();

		try {
			await this.pendingSync;
		} finally {
			this.pendingSync = null;
		}
	}

	private renderAndPublish() {
		const remaining = Math.max(0, this.deadlineTimeMs - this.serverTimeMs);
		this.remainingMs = remaining;

		let minutes = '00';
		let seconds = '00';
		if (remaining > 0) {
			const totalSeconds = Math.floor(remaining / 1000);
			const s = totalSeconds % 60;
			const m = Math.floor((totalSeconds / 60) % 60);
			minutes = m < 10 ? `0${m}` : String(m);
			seconds = s < 10 ? `0${s}` : String(s);
		}

		this.lastTick = { minutes, seconds, remainingMs: remaining };
		for (const sub of this.subscribers) {
			sub(this.lastTick);
		}
	}

	private startTick(localGeneration: number) {
		if (this.tickIntervalId) return;
		this.tickIntervalId = setInterval(() => {
			if (this.stopped || localGeneration !== this.generationId) return;

			// Simula avanço do relógio do servidor localmente
			this.serverTimeMs += 1000;
			this.renderAndPublish();

			if (this.remainingMs === 0) {
				this.clearTimers();
				void this.verifyZeroAndMaybeExpire(localGeneration);
			}
		}, 1000);
	}

	private startPolling(localGeneration: number) {
		if (this.pollTimeoutId) return;
		const schedule = () => {
			if (this.stopped || localGeneration !== this.generationId) return;
			this.pollTimeoutId = setTimeout(async () => {
				this.pollTimeoutId = undefined;
				if (this.stopped || localGeneration !== this.generationId) return;
				await this.syncNow(localGeneration);
				if (this.stopped || localGeneration !== this.generationId) return;
				this.renderAndPublish();
				schedule();
			}, 30000);
		};
		schedule();
	}

	private async verifyZeroAndMaybeExpire(localGeneration: number) {
		if (this.hasEmittedExpired) return;
		if (this.isVerifyingZero) return;
		this.isVerifyingZero = true;

		try {
			await this.syncNow(localGeneration);
			if (this.stopped || localGeneration !== this.generationId) return;

			// Importante: marcar expiração ANTES de publicar, para que o consumidor consiga
			// "claim" e executar o redirect/emit.
			if (this.remainingMs === 0) {
				this.hasEmittedExpired = true;
				this.renderAndPublish();
				// o componente que consome decide o que fazer (emitir evento / redirect)
				return;
			}
			this.renderAndPublish();

			// Se voltou a ter tempo, retoma
			this.startTick(localGeneration);
			this.startPolling(localGeneration);
		} finally {
			this.isVerifyingZero = false;
		}
	}
}

// Export helper (mais fácil de importar)
export const deadlineTimerEngine = DeadlineTimerEngine.instance();
